/*
  Admin API client. All requests use `credentials: 'include'` to send the
  httpOnly session cookie, and are namespaced under `/api/admin` or `/api/auth`.
  Never called from unauthenticated pages.
*/
const BASE = process.env.REACT_APP_BACKEND_URL;

async function req(path, options = {}) {
  const url = `${BASE}${path}`;
  const res = await fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {})
    }
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    const err = new Error(`HTTP ${res.status}: ${detail}`);
    err.status = res.status;
    err.body = detail;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

// ---------- auth ----------
export const authApi = {
  me: () => req("/api/auth/me"),
  exchangeSession: (sessionId) =>
    req("/api/auth/session", { method: "POST", body: JSON.stringify({ session_id: sessionId }) }),
  logout: () => req("/api/auth/logout", { method: "POST" })
};

// ---------- pricing ----------
export const pricingApi = {
  list: () => req("/api/admin/pricing"),
  patch: (id, patch) =>
    req(`/api/admin/pricing/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  history: (id) => req(`/api/admin/pricing/${id}/history`)
};

// ---------- quotes ----------
export const quoteApi = {
  list: (status) => req(`/api/admin/quotes${status ? `?status=${status}` : ""}`),
  get: (id) => req(`/api/admin/quotes/${id}`),
  create: (payload) =>
    req("/api/admin/quotes", { method: "POST", body: JSON.stringify(payload) }),
  patch: (id, patch) =>
    req(`/api/admin/quotes/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  remove: (id) => req(`/api/admin/quotes/${id}`, { method: "DELETE" }),
  preview: (id) => req(`/api/admin/quotes/${id}/preview`)
};

// ---------- commercial (Option C V1) ----------
// SBC Commercial layer. Owner/Admin only. providerCommercialRef and
// verticalCaseRef are populated via controlled endpoints and are never
// manually editable through the Quote Builder.
export const commercialApi = {
  createProvider: (payload) =>
    req("/api/admin/commercial/providers", { method: "POST", body: JSON.stringify(payload) }),
  listProviders: () => req("/api/admin/commercial/providers"),
  getProvider: (ref) => req(`/api/admin/commercial/providers/${ref}`),
  listAuthorisations: (query = {}) => {
    const qs = new URLSearchParams(query).toString();
    return req(`/api/admin/commercial/authorisations${qs ? `?${qs}` : ""}`);
  },
  getAuthorisation: (ref) => req(`/api/admin/commercial/authorisations/${ref}`),
  createAuthorisation: (payload) =>
    req("/api/admin/commercial/authorisations", { method: "POST", body: JSON.stringify(payload) }),
  supersedeAuthorisation: (ref, payload) =>
    req(`/api/admin/commercial/authorisations/${ref}/supersede`, { method: "POST", body: JSON.stringify(payload) }),
  cancelAuthorisation: (ref) =>
    req(`/api/admin/commercial/authorisations/${ref}/cancel`, { method: "POST" }),
  decideGateway: (payload) =>
    req("/api/admin/commercial/gateway", { method: "POST", body: JSON.stringify(payload) }),
  linkQuote: (quoteId, payload) =>
    req(`/api/admin/commercial/quotes/${quoteId}/link`, { method: "POST", body: JSON.stringify(payload) })
};
